import { useEffect, useState } from "react";
import { Outlet, useLocation, useNavigate, Navigate } from "react-router-dom";
import { api } from "../../lib/api";
import { useAuth } from "../../contexts/AuthContext";
import { hasCompletedPermissions } from "../PermissionsPage";
import { AppBootLoader } from "../../components/Loaders";
import NotificationGate from "../../components/NotificationGate";

/**
 * RiderLayout — gates the Rider PWA. Anyone not authenticated with role=RIDER
 * is bounced to /login?role=RIDER. Also pings GPS every 30s so dispatch can
 * see live rider positions on the map.
 */
export default function RiderLayout() {
    const { user, loading } = useAuth();
    const nav = useNavigate();
    const { pathname } = useLocation();
    // Recomputed on every render (localStorage-backed, not reactive
    // state) so both the redirect below AND the GPS effect see the
    // same, up-to-date answer.
    const permsReady = hasCompletedPermissions();

    useEffect(() => {
        if (loading) return;
        if (!user || user.role !== "RIDER") {
            nav("/login", { state: { role: "RIDER", from: pathname } });
        }
    }, [user, loading, nav, pathname]);

    // GPS pings so admin can see live rider positions.
    useEffect(() => {
        if (!user || user.role !== "RIDER" || !navigator.geolocation) return;
        // Wait for the onboarding screen to resolve permissions first.
        // This effect used to fire getCurrentPosition() unconditionally
        // the instant a rider logged in — regardless of whether they'd
        // been through /onboarding/permissions yet. React runs effects
        // even on a render where the component's JSX redirects
        // elsewhere, so this raced ahead of and silently consumed the
        // one-shot OS location-permission prompt before the rider ever
        // saw the onboarding screen's own explicit request (same root
        // cause as the notification-permission race fixed in
        // capacitor.js's setupPushNotifications() — user-reported).
        // Re-runs once ``permsReady`` flips true after onboarding
        // completes and the router lands back on a rider route.
        if (!permsReady) return;
        const tick = () => {
            navigator.geolocation.getCurrentPosition(
                (p) => api.post(`/rider/location?lat=${p.coords.latitude}&lng=${p.coords.longitude}`).catch(() => {}),
                () => {},
                { enableHighAccuracy: true, maximumAge: 20000, timeout: 8000 }
            );
        };
        tick();
        const id = setInterval(tick, 30000);
        return () => clearInterval(id);
    }, [user, permsReady]);

    if (loading || !user) {
        return <AppBootLoader />;
    }
    if (user.role !== "RIDER") return null;

    // First-launch permissions onboarding for rider app (location +
    // notifications + camera).
    if (!permsReady) {
        const nextUrl = pathname + (window.location.search || "");
        return <Navigate to={`/onboarding/permissions?app=rider&next=${encodeURIComponent(nextUrl)}`} replace />;
    }

    return (
        <div className="font-admin min-h-screen bg-muted/30 safe-pl safe-pr">
            <div className="px-3 pt-3"><NotificationGate /></div>
            <Outlet />
        </div>
    );
}
